import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  ArrowRight,
  Phone,
  Clock,
  Send,
  CheckCircle2,
  Building2,
  Calendar
} from "lucide-react";
import "./Contact.css";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";

// Contact Form with Spam Protection
const ContactForm = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    company: "",
    phone: "",
    message: "",
    website: "", // Honeypot field
    mathAnswer: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");
  
  // Simple math challenge for spam protection
  const num1 = 7;
  const num2 = 3;
  const correctAnswer = num1 + num2;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot check - if filled, it's likely a bot
    if (formData.website) {
      console.log("Bot detected");
      setIsSubmitted(true);
      return;
    }
    
    // Math verification
    if (parseInt(formData.mathAnswer) !== correctAnswer) {
      setError("Please answer the security question correctly.");
      return;
    }
    
    // Validate required fields
    if (!formData.firstName || !formData.lastName || !formData.message) {
      setError("Please fill in all required fields.");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          company: formData.company,
          phone: formData.phone,
          message: formData.message,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setIsSubmitted(true);
      } else {
        setError(data.message || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      console.error('Contact form error:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="contact-form-success">
        <CheckCircle2 size={48} className="success-icon" />
        <h3>Message Sent Successfully</h3>
        <p>Thank you for reaching out. Our team will get back to you within 1-2 business days.</p>
      </div>
    );
  }

  return (
    <form className="contact-form" onSubmit={handleSubmit}>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="firstName">First Name *</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            placeholder="John"
          />
        </div>
        <div className="form-group">
          <label htmlFor="lastName">Last Name *</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            placeholder="Smith"
          />
        </div>
      </div>
      
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="company">Company</label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            placeholder="Your Company"
          />
        </div>
        <div className="form-group">
          <label htmlFor="phone">Phone</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="(555) 123-4567"
          />
        </div>
      </div>
      
      {/* Honeypot field - hidden from users */}
      <input
        type="text"
        name="website"
        value={formData.website}
        onChange={handleChange}
        className="honeypot"
        tabIndex="-1"
        autoComplete="off"
      />
      
      <div className="form-group">
        <label htmlFor="message">Message *</label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows={5}
          placeholder="Tell us about your marketing goals and how we can help..."
        />
      </div>
      
      {/* Math challenge for spam protection */}
      <div className="form-group math-challenge">
        <label htmlFor="mathAnswer">Security Question: What is {num1} + {num2}? *</label>
        <input
          type="text"
          id="mathAnswer"
          name="mathAnswer"
          value={formData.mathAnswer}
          onChange={handleChange}
          required
          placeholder="Answer"
          className="math-input"
        />
      </div>
      
      {error && <div className="form-error">{error}</div>}
      
      <Button 
        type="submit" 
        className="btn-primary-zeta submit-btn"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Sending..." : "Send Message"}
        <Send size={18} />
      </Button>
    </form>
  );
};

// Contact Info Card
const ContactInfo = () => (
  <div className="contact-info-card">
    <h3>Get In Touch</h3>
    <p className="contact-info-intro">
      Ready to transform your marketing with data-driven precision? 
      Reach out to our team.
    </p>
    
    <div className="contact-details">
      <div className="contact-detail-item">
        <div className="contact-icon">
          <Building2 size={20} />
        </div>
        <div className="contact-detail-text">
          <strong>Headquarters</strong>
          <span>Metro Office Park</span>
          <span>14 Calle 2 102</span>
          <span>Guaynabo, Puerto Rico 00968</span>
        </div>
      </div>
      
      <div className="contact-detail-item">
        <div className="contact-icon">
          <Phone size={20} />
        </div>
        <div className="contact-detail-text">
          <strong>Phone</strong>
          <a href="tel:+18559484423">855-948-4423</a>
        </div>
      </div>
      
      <div className="contact-detail-item">
        <div className="contact-icon">
          <Clock size={20} />
        </div>
        <div className="contact-detail-text">
          <strong>Business Hours</strong>
          <span>Monday - Friday</span>
          <span>9:00 AM - 6:00 PM EST</span>
        </div>
      </div>
    </div>
    
    <div className="contact-meet-us">
      <div className="contact-icon">
        <Calendar size={20} />
      </div>
      <div className="contact-meet-text">
        <strong>Meet Us in Person</strong>
        <p>Attending an industry event? Check where you can connect with our team.</p>
        <Link to="/meet-us" className="meet-us-link">
          View Upcoming Events <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  </div>
);

// Hero Section
const ContactHero = () => (
  <section className="contact-hero">
    <div className="contact-hero-content">
      <span className="contact-label">CONTACT US</span>
      <h1>Let's Start a Conversation</h1>
      <p>
        Have questions about how our proprietary data and analytics can 
        drive results for your business? We're here to help.
      </p>
    </div>
  </section>
);

// Main Contact Section
const ContactSection = () => (
  <section className="contact-section">
    <div className="contact-container">
      <div className="contact-form-wrapper">
        <h2>Send Us a Message</h2>
        <p>Fill out the form below and our team will respond within 1-2 business days.</p>
        <ContactForm />
      </div>
      <div className="contact-info-wrapper">
        <ContactInfo />
      </div>
    </div>
  </section>
);

// Main Contact Page Component
const ContactPage = () => {
  return (
    <Layout>
      <div className="contact-page">
        <SEO 
          title="Contact Us - Get in Touch"
          description="Contact IntentLogic to learn how our data-driven marketing solutions can help your business. Schedule a consultation with our team of marketing experts."
          keywords="contact IntentLogic, marketing consultation, schedule demo, get in touch, marketing agency contact, data marketing services"
          canonical="/contact"
        />
        <ContactHero />
        <ContactSection />
      </div>
    </Layout>
  );
};

export default ContactPage;
